/**
 * How well does a food's name match what the user typed?
 *
 * This is the CLIENT-SIDE REPLICA of what Elasticsearch does for the same question. The app
 * searches the foods cached on the device as well as the ones the server returns, and it
 * re-scores BOTH with this module before showing them, so this rule has to resemble the index's
 * or the two halves of one list disagree about what a good match is.
 *
 * It replaced a rule that scored on shared three-letter fragments, which is why searching "Chai"
 * once returned Fried Chicken Breast and Cheddar Cheese — those names share the fragment "ch".
 * Whole stemmed words within an edit distance cannot make that mistake (#5114).
 *
 * WHAT IT MIRRORS, WHERE IT KNOWINGLY DIFFERS, AND WHY IT IS A REPLICA RATHER THAN SHARED CODE:
 * `design-docs/elasticsearch-infrastructure.md`, "How the client matches food names". Read that
 * before changing anything here — the two sides cannot share execution, so the agreement is held
 * only by that document and by the guard tests in food-search-match.test.ts, which sit under
 * "the assumptions this replica makes about the server".
 */

import { tokenizeFoodText } from '../food-text-tokenize';
import { newStemmer } from 'snowball-stemmers';

/** Everything this module borrows, passed in rather than imported, so it loads under the node
 *  test runner and its test can drive the real code. Same reasoning as food-search-elastic.ts. */
export interface FoodTextAnalyzerDeps {
  /** the English stemmer; in the app, `createFoodWordStemmer()` */
  stem: (word: string) => string;
  /** converts non-latin characters to latin; in the app, `transliterate` from transliteration */
  foldAccents: (text: string) => string;
}

/**
 * The stemmer that reduces a word to the same root the foods index does.
 *
 * The index's `english_stemmer` filter asks for `"language": "english"`, which Elasticsearch
 * resolves to Lucene's ORIGINAL Porter algorithm. Snowball's `english` is the later Porter2, and
 * the two disagree on some words ("generously" is `gener` in Porter and `generous` in Porter2),
 * so a food could match on the server and not on the device. Snowball's `porter` is the original
 * algorithm as Porter published it.
 *
 * Lucene follows Porter's own reference code instead, which leaves words of 1-2 letters alone.
 * Snowball's `porter` does not ("as" becomes "a", "s" becomes nothing), so those are skipped here.
 * The reference code's two other departures are NOT copied; see the divergence table in
 * `design-docs/elasticsearch-infrastructure.md`. The guard test in food-search-match.test.ts fails
 * if the index's language changes.
 */
export function createFoodWordStemmer(): (word: string) => string {
  const stemmer = newStemmer('porter');
  // if the word is 1-2 letters, keep it whole, as Lucene's PorterStemmer does
  return word => (word.length <= 2 ? word : stemmer.stem(word));
}

/**
 * Turn text into the list of words the matcher compares, the way the index does it.
 *
 * Mirrors the analyzer chain in `elasticsearch/index-foods.json`: lowercase, split into words,
 * drop a possessive ending, then stem. The accent folding is ours alone: the index has no
 * `asciifolding` filter, so it keeps "crème" where this turns it into "creme".
 *
 * The splitting half is `tokenizeFoodText` in `@cf-shared/food-text-tokenize`, shared with the
 * tool that builds the word dictionary. That tool needs the words BEFORE they are stemmed,
 * because it shows them to a person and "chocol" is not a word anybody typed (#5319). Sharing
 * the split is what stops the dictionary holding words this function can never produce.
 */
export function analyzeFoodText(text: string, deps: FoodTextAnalyzerDeps): string[] {
  return tokenizeFoodText(text, deps.foldAccents)
    .map(word => deps.stem(word))
    .filter(word => word.length > 0);
}

/**
 * Are these two words close enough to be treated as the same one, allowing at most `maxEdits`
 * single-letter insertions, deletions, substitutions or swaps of neighbouring letters?
 *
 * A SWAP COUNTS AS ONE EDIT, not two. That is what Elasticsearch does: its fuzzy matching has
 * `fuzzy_transpositions` switched on unless told otherwise, and nothing in our query turns it
 * off. Counting a swap as two would lose the commonest typo of all at the length where the
 * budget is only one edit, so "mlik" would find Milk on the server and nothing here.
 *
 * It gives up as soon as the answer is known to be over budget. That is not only speed: every
 * caller asks "is this within N edits", so a distance of 9 and a distance of 40 are the same
 * answer, and finishing the sum for two long unrelated words is wasted on every keystroke.
 */
function isWithinEdits(a: string, b: string, maxEdits: number): boolean {
  // a length gap wider than the budget cannot be closed, whatever the letters are
  if (Math.abs(a.length - b.length) > maxEdits) {
    return false;
  }
  // the swap rule needs the row before last as well as the last one
  let twoRowsBack: number[] | undefined = undefined;
  let previousRow = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const currentRow = [i];
    let bestInRow = i;
    for (let j = 1; j <= b.length; j++) {
      const substitution = previousRow[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1);
      let cost = Math.min(previousRow[j] + 1, currentRow[j - 1] + 1, substitution);
      // if these two letters are the previous two in the other word, the other word has them
      // the other way round, and undoing that is one edit
      if (i > 1 && j > 1 && twoRowsBack && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        cost = Math.min(cost, twoRowsBack[j - 2] + 1);
      }
      currentRow.push(cost);
      bestInRow = Math.min(bestInRow, cost);
    }
    // every later row can only add to the best figure in this one, so once the whole row is over
    // budget the final answer is too
    if (bestInRow > maxEdits) {
      return false;
    }
    twoRowsBack = previousRow;
    previousRow = currentRow;
  }
  return previousRow[b.length] <= maxEdits;
}

/**
 * How many letter-edits Elasticsearch's `fuzziness: AUTO` allows for a word of this length:
 * none for a very short word, one for a short one, two for anything longer. Short words get no
 * slack because at two or three letters almost everything is one edit from everything else.
 *
 * Exported because word completion uses the SAME budget when it looks for a dictionary word the
 * user has mistyped (#5319). One rule in one place: if the matcher's idea of "close enough"
 * ever changes, the completions change with it rather than drifting apart from it.
 */
export function allowedEdits(term: string): number {
  if (term.length <= 2) {
    return 0;
  }
  return term.length <= 5 ? 1 : 2;
}

/** Is this word in the food's name, either exactly or near enough to be a typo of one? */
function nameAnswersWord(word: string, nameTerms: string[]): boolean {
  return nameTerms.includes(word)
    || nameTerms.some(nameTerm => isWithinEdits(word, nameTerm, allowedEdits(word)));
}

/**
 * How well one typed word is answered by a food's words. 0 when it is not answered at all.
 *
 * `alternatives` is only passed for the LAST typed word, and holds the whole words that word may
 * be standing in for — it is the one word the user may still be half-way through typing, so
 * `completeFoodQuery` finishes it off against the dictionary of real food words first (#5319).
 *
 * There used to be a third rule here: the last word could match as a BEGINNING of any word in
 * the name. That was a rule the Elasticsearch server does not have — its query builds no prefix
 * clause of any kind — so the client and the server judged the same text differently, and the
 * client decides the order of BOTH lists. Completing the word replaces it: the comparison is
 * now always between whole words, which is the rule both sides share. The visible gain is that
 * an invented brand spelling like "Chocomize" no longer answers "choc", because it is not a
 * word anybody could complete "choc" to.
 */
function scoreTerm(
  queryTerm: string,
  nameTerms: string[],
  alternatives: string[] | undefined,
): number {
  if (nameTerms.includes(queryTerm)) {
    return EXACT_WORD;
  }
  if (nameTerms.some(nameTerm => isWithinEdits(queryTerm, nameTerm, allowedEdits(queryTerm)))) {
    return INEXACT_WORD;
  }
  // a completion is a GUESS at what the user meant to type, so however well it fits it can never
  // count as an exact match -- otherwise a food found by guessing would outrank one found by
  // what the person actually typed
  // only the last typed word has alternatives
  for (const alternative of alternatives ?? []) {
    // the word as typed is the first alternative and was already tried by the two rules above
    if (alternative !== queryTerm && nameAnswersWord(alternative, nameTerms)) {
      return INEXACT_WORD;
    }
  }
  return 0;
}

/** A typed word found as-is in the name. */
const EXACT_WORD = 1;
/** A typed word reached only by correcting a letter or by finishing it off. */
const INEXACT_WORD = 0.7;

/**
 * How well `nameTerms` answers `queryTerms`, from 0 (nothing matched) to 1 (every typed word
 * matched exactly). Both arguments come from `analyzeFoodText`.
 *
 * A score of 0 means "do not show this food at all" — it replaces the tuned 0.2 cut-off the
 * fragment rule needed, which no longer has anything to be a cut-off ON. A food is kept when ANY
 * typed word is answered, which is what Elasticsearch does too (its `match` ORs the words
 * together). Being permissive is safe here because the score, not the filter, decides the order:
 * a food answering one word of two scores half as much as one answering both, and sinks.
 */
export function scoreFoodNameMatch(
  queryTerms: string[],
  nameTerms: string[],
  lastTermAlternatives?: string[],
): number {
  return scoreFoodNameMatchInDetail(queryTerms, nameTerms, lastTermAlternatives).score;
}

/**
 * The same score, plus HOW MANY of the typed words the name answered at all (#5410).
 *
 * The three say different things and the food list needs all of them. The score says how WELL the
 * name answered — a word reached by correcting a letter counts less than one found as typed.
 * `answeredWords` says how MUCH of what was typed the name accounts for, and a word answered
 * inexactly still counts: somebody who typed three words means all three, however they spelled
 * them. `exactWords` says how much of that was answered with no letter changed at all, which is
 * what separates a food that really is what you typed from one that is merely close to it.
 * `extraWords` says how much of the NAME is not about what was typed (#4970): "Oatmeal" has none
 * for "oatmeal", "Oatmeal Pie" has one.
 */
export function scoreFoodNameMatchInDetail(
  queryTerms: string[],
  nameTerms: string[],
  lastTermAlternatives?: string[],
): { score: number, answeredWords: number, exactWords: number, extraWords: number } {
  // no words to answer means nothing matched. This is NOT the "show me my history" case: text
  // made only of punctuation analyses to no words too, and listing the entire cache as a perfect
  // match for "???" would be worse than the bug being fixed here. Only the caller can tell the
  // two apart, because only it can see that the search box itself is empty.
  if (queryTerms.length === 0) {
    return { score: 0, answeredWords: 0, exactWords: 0, extraWords: 0 };
  }
  let total = 0;
  let answeredWords = 0;
  let exactWords = 0;
  for (let i = 0; i < queryTerms.length; i++) {
    const isLastTypedTerm = i === queryTerms.length - 1;
    const termScore = scoreTerm(queryTerms[i], nameTerms, isLastTypedTerm ? lastTermAlternatives : undefined);
    total += termScore;
    if (termScore > 0) {
      answeredWords++;
    }
    // scoreTerm answers EXACT_WORD or INEXACT_WORD and nothing in between, so this counts the
    // words found as typed rather than reached by changing or finishing a letter
    if (termScore === EXACT_WORD) {
      exactWords++;
    }
  }
  return { score: total / queryTerms.length, answeredWords, exactWords, extraWords: countExtraWords(queryTerms, nameTerms, lastTermAlternatives) };
}

/**
 * How many of the name's words answer none of the typed words (#4970).
 *
 * A name word counts as answering when a typed word is in it as typed or within the typing-error
 * budget, so "Chicken breasts" has nothing extra for "chicken breast" (the stems are the same).
 * The last typed word's completions count only when that word as typed is not in the name -- the
 * order `scoreTerm` tries them in. Otherwise "egg" would make "Egg Eggplant Salad" look as plain
 * as "Egg", because "eggplant" is one of the words "egg" may be completing to.
 */
function countExtraWords(queryTerms: string[], nameTerms: string[], lastTermAlternatives: string[] | undefined): number {
  const lastTypedTerm = queryTerms[queryTerms.length - 1];
  // if the last word as typed is in the name, it is not standing in for a completion
  const typedWords = lastTermAlternatives && !nameAnswersWord(lastTypedTerm, nameTerms)
    ? [...queryTerms, ...lastTermAlternatives]
    : queryTerms;
  let extraWords = 0;
  for (const nameTerm of nameTerms) {
    // if no typed word answers this name word, the name is saying something besides the search
    if (!typedWords.some(typedWord => nameAnswersWord(typedWord, [nameTerm]))) {
      extraWords++;
    }
  }
  return extraWords;
}

/**
 * How much of a match on an ALTERNATIVE name is worth, compared with the same match on the
 * food's main name. Mirrors the `boost: 0.8` the server puts on its `match` clause over
 * `altNames` (see `buildFoodNameSearchQueryBody` in food-search-query.ts): both sides rank a food
 * found by its second name below an equally good match on the name it is normally called.
 *
 * The server ALSO boosts a phrase hit on the alternative names by 1.6, and that is not mirrored
 * here, because this matcher has no notion of words being next to each other at all. That is a
 * separate row in the divergence table in the design doc.
 */
const ALT_NAME_WEIGHT = 0.8;

/** What a food's names, taken together, made of the typed text. */
export interface FoodNameMatch {
  /** the score, from 0 (show this food to nobody) to 1 (every typed word matched exactly) */
  score: number;
  /** how many of the typed words this food's names answered at all, exactly or not (#5410) */
  answeredWords: number;
  /** how many of those it answered with no letter changed or finished off (#5410) */
  exactWords: number;
  /** how many words of the winning name answer none of the typed words (#4970) */
  extraWords: number;
  /** the alternative name that produced the score, when an alternative name beat the main one */
  matchedAltName?: string;
}

/** How much a match on one of a food's alternative names is worth. */
export enum AltNameMatch {
  /** below the same match on the main name, as the server weights it; every food but a staple */
  Discounted = 'discounted',
  /**
   * exactly as much as the main name, with no word of the alternative name counted as extra (food
   * staples plan 3). A staple's aliases are the plain words people search ("chicken fillet" on
   * "Chicken breast, boneless, skinless, raw"), written by our curator, so a search that hits one is
   * asking for that staple. Without this, an Open Food Facts food NAMED "Chicken fillet" answered
   * more typed words than the staple and was shown above it, whatever the source tiers said.
   */
  SameAsName = 'same-as-name',
}

/** true when `candidate` answers the search better than `best`: more words, then more exact, then fewer extra */
function answersBetter(candidate: FoodNameMatch, best: FoodNameMatch): boolean {
  if (candidate.answeredWords !== best.answeredWords) {
    return candidate.answeredWords > best.answeredWords;
  }
  if (candidate.exactWords !== best.exactWords) {
    return candidate.exactWords > best.exactWords;
  }
  return candidate.extraWords < best.extraWords;
}

/**
 * How well a food answers the typed text, reading its alternative names as well as its name.
 *
 * A food can carry other names it is known by — a second spelling, or the other name a brand
 * sells the same product under (`Food.altNames`). The server searches those too, so a search
 * that only read the main name would find fewer of the user's own cached foods than the same
 * search run against the server — and a cached food scoring 0 is not merely sorted last, it is
 * dropped from the list. That is worst exactly when the cache is all there is: offline, or
 * while the food server cannot be reached (df-cc8cd29c01ca).
 *
 * The best of the names wins, rather than the sum: a food is not a better answer for being
 * known by several names.
 *
 * `analyzedAltNames` lets a caller hand in each alias already split and stemmed. That is the whole
 * cost control behind keeping the staples on the device (food staples plan 4c, #5625): on the
 * `SameAsName` path there is no "a perfect name match cannot be beaten" early exit, so EVERY alias
 * of every staple is analysed on every offline search — and the staples carry more aliases than
 * names. It changes no answer; it only avoids redoing work the caller already did.
 *
 * With `AltNameMatch.SameAsName` an alternative name wins when it answers the search better in the
 * order the food list uses (more typed words, then more exact words, then fewer extra words), and
 * its extra words count as 0 -- so "bread" reaching "Cornbread" through its alias "corn bread" ties
 * with a plain "Bread", as the owner chose (2026-09-15). Word order plays no part on either path.
 */
export function scoreFoodMatch(
  queryTerms: string[],
  nameTerms: string[],
  altNames: string[] | undefined,
  deps: FoodTextAnalyzerDeps,
  lastTermAlternatives?: string[],
  altNameMatch: AltNameMatch = AltNameMatch.Discounted,
  analyzedAltNames?: string[][],
): FoodNameMatch {
  const nameMatch = scoreFoodNameMatchInDetail(queryTerms, nameTerms, lastTermAlternatives);
  if (!altNames) {
    return nameMatch;
  }
  // Each alias split and stemmed already, when the caller had them to hand (food staples plan
  // 4c, #5625). Only used when there is one entry per alias: the two are matched up by
  // position, so a length that disagrees means they describe different lists and the words
  // would be attached to the wrong names. Falling back to analysing is always correct, just
  // slower, which is the right way round for a cost control to fail.
  const analyzed = analyzedAltNames?.length === altNames.length ? analyzedAltNames : undefined;
  const wordsOf = (index: number) => analyzed?.[index] ?? analyzeFoodText(altNames[index], deps);
  if (altNameMatch === AltNameMatch.SameAsName) {
    let best: FoodNameMatch = nameMatch;
    for (let index = 0; index < altNames.length; index++) {
      const altName = altNames[index];
      const alt = scoreFoodNameMatchInDetail(queryTerms, wordsOf(index), lastTermAlternatives);
      // if the alias answered nothing, it cannot be the better answer
      if (alt.answeredWords === 0) {
        continue;
      }
      const candidate: FoodNameMatch = { score: alt.score, answeredWords: alt.answeredWords, exactWords: alt.exactWords, extraWords: 0, matchedAltName: altName };
      if (answersBetter(candidate, best)) {
        best = candidate;
      }
    }
    return best;
  }
  // a perfect match on the name cannot be beaten by anything weighted below 1, so the
  // alternative names need not be analysed at all
  if (nameMatch.score >= 1) {
    return nameMatch;
  }
  let best: FoodNameMatch = nameMatch;
  for (let index = 0; index < altNames.length; index++) {
    const altName = altNames[index];
    const alt = scoreFoodNameMatchInDetail(queryTerms, wordsOf(index), lastTermAlternatives);
    const altScore = ALT_NAME_WEIGHT * alt.score;
    if (altScore > best.score) {
      best = { score: altScore, answeredWords: alt.answeredWords, exactWords: alt.exactWords, extraWords: alt.extraWords, matchedAltName: altName };
    }
  }
  return best;
}

/** What the food list needs to know about one food in order to put it in order (#5410). */
export interface RankedFood {
  /** how many typed words this food's names answered at all */
  answeredWords?: number;
  /** how many of those it answered with no letter changed */
  exactWords?: number;
  /** how many of its name's words answer none of the typed words */
  extraWords?: number;
  /** the food's source tier, lower first (`food-search-tiers.ts`); unset while source tiers are off */
  tier?: number;
  /** the name match weighted by how recently the food was used */
  match?: number;
  /** Elasticsearch's own score, on a food the server found; absent on a food read from the device */
  score?: number;
}

/**
 * Which of two foods the list shows first (#5410). Sort with it: it answers a negative number
 * when `a` goes first.
 *
 * **Foods that answer more of what was typed come first, whatever the person has logged before.**
 * Before this rule, how recently a food had been used multiplied its name match by anything from
 * 0.2 to 1, so a food logged last week that answered two of three typed words beat a food nobody
 * had logged that answered all three — searching "breaded chicken breast" put "Chicken Breast Raw"
 * and even "Chicken, ground, raw" above every breaded chicken breast in the database.
 *
 * Inside one group — the foods that answered the same number of words — the old rule still
 * decides, and that is what keeps history useful: `match` is the name match multiplied by the
 * recency weight, so of two equally complete answers the one you logged recently comes first.
 * Elasticsearch's own score breaks a remaining tie between two foods the server found, and a food
 * already on the device goes ahead of a server one when everything else is equal.
 */
export function compareRankedFoods(a: RankedFood, b: RankedFood): number {
  const answered = (b.answeredWords ?? 0) - (a.answeredWords ?? 0);
  if (answered !== 0) {
    return answered;
  }
  // Among foods answering the same amount, the ones that answered it AS TYPED come first. This is
  // the same rule as the line above, one level finer: answering a word exactly is answering it
  // better than reaching it by changing a letter, so it belongs with "how well does this food
  // answer the search" rather than being left to how recently the food was logged.
  //
  // It matters because the recency weight below spans 0.2 to 1 while an exact word is worth 1
  // against a corrected word's 0.7 -- so without this, searching "brocoli" put a "Broccoli" the
  // person had logged (0.7 x 0.5 = 0.35) above the "Brocoli" foods they had literally typed the
  // name of (1 x 0.2 = 0.2). The owner reported exactly that on 2026-09-12.
  const exact = (b.exactWords ?? 0) - (a.exactWords ?? 0);
  if (exact !== 0) {
    return exact;
  }
  // Among foods answering the search equally well, the one from the better source comes first
  // (#5550): recently used, own foods, staples, trusted sources, other people's foods, and Open
  // Food Facts last. Searching "chicken breast", 25 of the server's 50 foods are Open Food Facts
  // entries, many named exactly "Chicken breast" with wrong macros; the fewer-extra-words rule
  // below lifted them above "Boneless Skinless Chicken Breast, Raw", so the source has to decide
  // before name length does. It comes AFTER the two word rules so that a search only one source
  // answers still finds it: "oreo" answers no trusted food, and Open Food Facts Oreos stay on top.
  // A food with no tier ties here, which is how the empty search box keeps its pure recency order.
  if (a.tier !== undefined && b.tier !== undefined && a.tier !== b.tier) {
    return a.tier - b.tier;
  }
  // Among foods answering the search equally well, the one whose name says LESS besides it comes
  // first (#4970): "Oatmeal" before "Oatmeal Pie", "Chicken breast" before "Fried Chicken Breast".
  // Searching those, every result answers every typed word exactly, so without this the order
  // fell to the server's score, which carries each food's popularity boost and put plain oatmeal
  // 10th. It comes before recency for the same reason as the two rules above -- it is about how
  // well the food answers the search -- and because otherwise a logged "Oatmeal breakfast bowl"
  // (1 x 1) still beats a never-logged "Oatmeal" (1 x 0.2), which is what Menno saw.
  // A food with no count ties here: the Foods page fills the count in only for a typed or barcode
  // search, together with the source tier above, because without the tier this rule lifts junk
  // Open Food Facts entries named exactly like the search above better foods.
  //
  // Only for foods that answered part of the search. A barcode search, or a server hit found by a
  // name the list cannot see, answers no typed word, so every word of its name would count as extra
  // and that list would come out sorted by the shortest name instead of the server's score.
  // (answeredWords is the same for both foods by this point.)
  const extra = (a.extraWords ?? 0) - (b.extraWords ?? 0);
  if ((a.answeredWords ?? 0) > 0 && extra !== 0) {
    return extra;
  }
  const match = (b.match ?? 0) - (a.match ?? 0);
  if (match !== 0) {
    return match;
  }
  // a food on the device has no server score; it goes first, as it did when the two lists were
  // merged by hand
  if ((a.score === undefined) !== (b.score === undefined)) {
    return a.score === undefined ? -1 : 1;
  }
  return (b.score ?? 0) - (a.score ?? 0);
}
